import React, { ChangeEvent, FocusEvent, useState } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import { IconEnum } from '@investoro/core';
import styled from 'styled-components';

import Icon from 'components/common/Icon';
import TextInput from 'components/common/Inputs/TextInput';

const CardContainer = styled.div<{ $borderColor: string }>`
    display: flex;
    flex-direction: column;
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 16px;
    min-width: 200px;
`;

const CardTitle = styled.h3`
    font-size: var(--font-size-body-2);
    font-weight: var(--font-weight);
    color: var(--color-primary);
    margin: 0 0 12px 0;
`;

const RequiredMark = styled.span`
    color: var(--color-error);
    margin-left: 4px;
`;

const Dot = styled.span<{ $color: string }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) => props.$color};
    display: inline-block;
    margin-right: 8px;
`;

const InputsRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-end;
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const InputLabel = styled.span`
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    margin-bottom: 4px;
`;

const ErrorText = styled.div`
    font-size: 11px;
    color: var(--color-error);
    margin-top: 4px;
`;

const ResetButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 50px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;

    &:hover {
        opacity: 0.7;
        background: transparent;
    }

    &:active {
        background: transparent;
    }

    .icon {
        width: 16px;
        height: 16px;
    }
`;

export interface CostCardProps {
    title: string;
    type?: 'text' | 'date' | 'string';
    fieldName: string;
    customColor?: string;
    value: string | number | null | undefined;
    totalValue?: string | number | null;
    suffix?: string;
    totalValueSuffix?: string;
    valueLabel?: string;
    totalLabel?: string;
    placeholder?: string;
    totalPlaceholder?: string;
    showTotalValue?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    totalDisabled?: boolean;
    required?: boolean;
    isError?: boolean;
    errorMessage?: string;
    resetIcon?: IconEnum;
    onValueChange?: (fieldName: string, value: string | number) => void;
    onValueBlur?: (fieldName: string, value: string | number) => void;
    onResetField?: (fieldName: string) => void;
    onResetTotalField?: (fieldName: string) => void;
    onTotalValueChange?: (fieldName: string, value: string | number) => void;
    onTotalValueBlur?: (fieldName: string, value: string | number) => void;
}

const CostCard: React.FC<CostCardProps> = ({
    title,
    fieldName,
    customColor,
    value,
    totalValue,
    suffix = 'zł',
    totalValueSuffix,
    valueLabel,
    totalLabel,
    placeholder,
    totalPlaceholder = '0',
    disabled = false,
    readOnly = false,
    totalDisabled = false,
    required = false,
    isError: externalError = false,
    errorMessage,
    type = 'text',
    resetIcon = IconEnum.RELOAD,
    showTotalValue,
    onValueChange,
    onValueBlur,
    onResetField,
    onTotalValueChange,
    onTotalValueBlur
}) => {
    const [touched, setTouched] = useState(false);

    const totalSuffix = totalValueSuffix ?? suffix;
    const isNumeric = type !== 'date' && type !== 'string';
    const defaultPlaceholder = placeholder !== undefined ? placeholder : isNumeric ? '0' : '';

    const numericValue = typeof value === 'number' ? value : parseFloat(String(value || '0')) || 0;
    const localError = touched && required && numericValue === 0;
    const hasError = localError || externalError;
    const displayErrorMessage = hasError
        ? externalError && errorMessage || (localError ? 'Wartość nie może być pusta' : undefined)
        : undefined;

    const handleNumericValueChange = (values: NumberFormatValues) => {
        onValueChange?.(fieldName, values.value);
    };

    const handleValueChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onValueChange?.(fieldName, e.target.value);
    };

    const handleValueBlur = (e: FocusEvent<HTMLInputElement | HTMLDivElement | HTMLTextAreaElement>) => {
        const target = e.target as HTMLInputElement;
        setTouched(true);
        onValueBlur?.(fieldName, target.value);
    };

    const handleNumericTotalChange = (values: NumberFormatValues) => {
        onTotalValueChange?.(fieldName, values.value);
    };

    const handleTotalChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onTotalValueChange?.(fieldName, e.target.value);
    };

    const handleTotalBlur = (e: FocusEvent<HTMLInputElement | HTMLDivElement | HTMLTextAreaElement>) => {
        const target = e.target as HTMLInputElement;
        onTotalValueBlur?.(fieldName, target.value);
    };

    const handleReset = () => {
        onResetField?.(fieldName);
    };

    const handleFocus = (e: FocusEvent<Element>) => {
        const target = e.target as HTMLInputElement;

        if (target && typeof target.select === 'function') {
            target.select();
        }
    };

    return (
        <CardContainer $borderColor={customColor}>
            <CardTitle>
                {customColor && <Dot $color={customColor} />}
                {title}
                {required && <RequiredMark>*</RequiredMark>}
            </CardTitle>
            <InputsRow>
                <InputGroup>
                    {valueLabel && <InputLabel>{valueLabel}</InputLabel>}
                    {isNumeric
                        ? <NumericFormat
                            value={value ?? ''}
                            onValueChange={handleNumericValueChange}
                            onBlur={handleValueBlur}
                            onFocus={handleFocus}
                            placeholder={defaultPlaceholder}
                            suff={suffix}
                            customInput={TextInput}
                            thousandSeparator=" "
                            decimalSeparator=","
                            decimalScale={2}
                            allowNegative={false}
                            isError={hasError}
                            disabled={disabled}
                            readOnly={readOnly}
                        />
                        : <TextInput
                            value={value ?? ''}
                            onChange={handleValueChange}
                            onBlur={handleValueBlur}
                            onFocus={handleFocus}
                            placeholder={defaultPlaceholder}
                            suff={suffix}
                            type={type}
                            isError={hasError}
                            disabled={disabled}
                            readOnly={readOnly}
                        />
                    }
                </InputGroup>
                {showTotalValue && <InputGroup>
                    {totalLabel && <InputLabel>{totalLabel}</InputLabel>}
                    {isNumeric
                        ? <NumericFormat
                            value={totalValue ?? ''}
                            onValueChange={handleNumericTotalChange}
                            onBlur={handleTotalBlur}
                            onFocus={handleFocus}
                            placeholder={totalPlaceholder}
                            suff={totalSuffix}
                            customInput={TextInput}
                            thousandSeparator=" "
                            decimalSeparator=","
                            decimalScale={2}
                            allowNegative={false}
                            disabled={totalDisabled}
                            readOnly={totalDisabled}
                        />
                        : <TextInput
                            value={totalValue ?? ''}
                            onChange={handleTotalChange}
                            onBlur={handleTotalBlur}
                            onFocus={handleFocus}
                            placeholder={totalPlaceholder}
                            suff={totalSuffix}
                            type={type}
                            disabled={totalDisabled}
                            readOnly={totalDisabled}
                        />
                    }
                </InputGroup>}
                {onResetField &&
                    <ResetButton onClick={handleReset} title="Resetuj łącznie">
                        <Icon icon={resetIcon} />
                    </ResetButton>
                }
            </InputsRow>
            {displayErrorMessage && <ErrorText>{displayErrorMessage}</ErrorText>}
        </CardContainer>
    );
};

export default CostCard;
